import {
  Button,
  ButtonVariant,
  Modal,
} from '@usealloy/component-library';
import kebabCase from 'lodash/kebabCase';
import React from 'react';
import startCase from 'lodash/startCase';
import { useFormContext } from 'react-hook-form';

import type { Action } from 'flow/store/engine/types';
import type {
  FormState,
} from 'flow/editor/components/NodeEditorDrawer/AttributeConfiguration/InputAttributeConfigurationTab/types';
import type {
  FormState as NodeEditorDrawerFormState,
} from 'flow/editor/components/NodeEditorDrawer/useNodeEditorForm';
import { getActionName, getActionTypeText } from 'flow/service/actions';
import type { useModalGroup } from 'flow/editor/hooks';

import type { FooterModalsNames } from '../types';
import { StyledContent, StyledList } from './styles';


interface ConfirmDataTypeChangeModelProps {
  focusedAction: Action;
  focusedActionDependents: Action[];
  isOpen: boolean;
  modalGroup?: ReturnType<typeof useModalGroup<FooterModalsNames>>;
  onSave(data: NodeEditorDrawerFormState): void;
}

export default function ConfirmDataTypeChangeModal({
  focusedAction,
  focusedActionDependents,
  isOpen,
  modalGroup,
  onSave,
}: ConfirmDataTypeChangeModelProps) {
  const formMethods = useFormContext<FormState>();

  const actionType = getActionTypeText(focusedAction);

  const dependentNames = focusedActionDependents.map(dependent => {
    const dependentName = getActionName(dependent);

    // We do this because there is a chance getActionName could return an
    // empty string and in that case we don't want to show the user; we should
    // ideally throw an error instead as getActionName says in its comments
    if (dependentName.length) {
      return `${startCase(dependent.type)}: ${dependentName}`;
    }

    return undefined;
  }).filter(name => name);

  const onClose = React.useCallback(() => {
    formMethods.resetField('dataType');
    modalGroup?.close();
  }, [formMethods, modalGroup]);

  return (
    <Modal
      hasCloseIcon={false}
      header={`Are you sure you want to update this ${actionType} Attribute data type?`}
      isOpen={isOpen}
      onClose={onClose}
      primaryAction={(
        <Button
          onClick={formMethods.handleSubmit(onSave)}
          variant={ButtonVariant.primary}
        >
          Update
        </Button>
      )}
      secondaryAction={(
        <Button onClick={onClose} variant={ButtonVariant.secondary}>
          Cancel
        </Button>
      )}
      size="small"
    >
      <StyledContent>
        Doing so will automatically update the values of the following dependent nodes
        <StyledList>
          {dependentNames.map(name => (
            <li key={kebabCase(name)}>{name}</li>
          ))}
        </StyledList>
      </StyledContent>
    </Modal>
  );
}
